import { plainToClass } from "class-transformer";
import { DateUpdatedSection } from "../../../../shared/components/date-updated-section/date-updated-section.view";
import { Appendix } from "../../../../shared/entities/scripts/appendix";
import { Site } from "../../../../shared/entities/scripts/site";
import { Study } from "../../../../shared/entities/scripts/study";
import { DoArraysOverlap, Unique } from "../../../../shared/scripts/array-functions";
import { SortDates } from "../../../../shared/scripts/date-functions";
import { IStudyAppendixSiteFilterParent, StudyAppendixSiteFilterSection } from "../../../../shared/scripts/filtering/filtering.study-appendix-site";
import { ResetDropdownFilters } from "../../../../shared/scripts/filtering/filtering.utilities";
import { FormatNumberWithCommas } from "../../../../shared/scripts/number-functions";
import { ForecastGraphDataPoint } from "../../forecasts/scripts/forecasts.entities";
import { SiteInfo } from "../../sites/shared/sites.entities";
import { ChartController, ChartSetup, ChartSeries, ChartDataPoint } from "./enrollment.chart-controller";
import { AggregatedParticipants, Participant } from "./enrollment.entities";
import { EnrollmentGauges } from "./enrollment.gauges";
import { EnrollmentLookup } from "./enrollment.lookup";
import { ProgressBarSection } from "./enrollment.progress-bars";
import { DrilldownChartBar } from './enrollment.drilldown-bar-chart';
import { ChartBar } from "./enrollment.chart-bar";
import { ChartLine } from "./enrollment.chart-line";

export class EnrollmentPage implements IStudyAppendixSiteFilterParent, IEnrollmentPageData {

    allStudies: Study[] = [];
    allAppendices: Appendix[] = [];

    consentedSites: Site[] = [];
    activatedSites: Site[] = [];
    //siteActivationStudyTimestampMap: Map<number, number[]> = new Map<number, number[]>(); //key: studyId, value: array of site activation timestamps
    sitesActivatedByMonthByStudy: Map<string, number> = new Map<string, number>(); //key = studyId_dateTimestamp, value = count

    allParticipants: Participant[];
    selectedParticipants: Participant[];
    aggregatedParticipants: AggregatedParticipants = new AggregatedParticipants();
    aggParticipantsByStudy: Map<number, AggregatedParticipants> = new Map<number, AggregatedParticipants>();
    aggParticipantsByAppendix: Map<number, AggregatedParticipants> = new Map<number, AggregatedParticipants>();
    aggParticipantsBySite: Map<number, AggregatedParticipants> = new Map<number, AggregatedParticipants>(); 

    forecastRandomized: ForecastGraphDataPoint[];
    selectedForecastRandomized: ForecastGraphDataPoint[];

    filter: StudyAppendixSiteFilterSection;
    progressBars: ProgressBarSection;
    gauges: EnrollmentGauges;
    chartController: ChartController;

    selectedPopulation: string; //consented, randomized, screen-fail
    selectedBreakdown: string; // "Overall", "Race", "Age", etc...
    selectedAggregation: string; // overall, ByStudy, ByAppendix, BySite
    selectedGraphView: string; //cumulative (line), pie chart, by month (bar), rate by month (bar)
    unassignedAppendixIds: number[] = [];
    showUnassigned: boolean = true;
    unassignedAppendicies: Appendix[] = [];


    totEnrollDrilldownChart: DrilldownChartBar = new DrilldownChartBar();
 
    constructor(siteInfoJson: any, participantJson: any, randomizedAspirationalJson: any, lastUpdatedJson: any) {

        $(() => {

            //console.log(siteInfoJson, participantJson, randomizedAspirationalJson, lastUpdatedJson);

            let foundSiteIds: string[] = [];
            let foundStudyIds: number[] = [];
            let foundAppendixIds: number[] = [];

            this.allParticipants = plainToClass(Participant, <Participant[]>participantJson);
            this.allParticipants = this.allParticipants.filter((p: Participant) => {
                p.initialize();

                if (p.Consented == 1) {
                    if (!foundSiteIds.includes(p.SiteNumber + "-" + p.StudyId)) { foundSiteIds.push(p.SiteNumber + "-" + p.StudyId); }

                    if (!foundStudyIds.includes(p.StudyId)) {
                        this.allStudies.push(new Study(p.StudyId, Study.GetStudyName(p.StudyId)));
                        foundStudyIds.push(p.StudyId);
                    }

                    if (!foundAppendixIds.includes(p.AppendixId)) {
                        if (Appendix.GetAppendixName(p.AppendixId).includes("Unassigned")) {
                            if (!this.unassignedAppendixIds.includes(p.AppendixId)) {
                                this.unassignedAppendixIds.push(p.AppendixId);
                                this.unassignedAppendicies.push(new Appendix(p.AppendixId, p.StudyId, Appendix.GetAppendixName(p.AppendixId)));
                            }
                            return true;
                        }
                        this.allAppendices.push(new Appendix(p.AppendixId, p.StudyId, Appendix.GetAppendixName(p.AppendixId)));
                        foundAppendixIds.push(p.AppendixId);
                    }

                    return true;
                }
                else return false;
            });

            let siteInfo: SiteInfo[] = SiteInfo.Parse(siteInfoJson);
            let consentedSiteMap: Map<number, Site> = new Map<number, Site>();
            let activatedSiteMap: Map<number, Site> = new Map<number, Site>();
            siteInfo.forEach((s: SiteInfo) => {
                if (foundSiteIds.includes(s.SiteNumber + "-" + s.StudyId)) {
                    if (consentedSiteMap.has(s.SiteNumber)) {
                        consentedSiteMap.get(s.SiteNumber).StudyIds.push(s.StudyId);
                    }
                    else {
                        let newSite: Site = new Site(s.SiteNumber, s.getSiteNameNumber());
                        newSite.StudyIds.push(s.StudyId);
                        consentedSiteMap.set(s.SiteNumber, newSite);
                    }
                }

                if (s.ActivationDate != undefined) {
                    s.setActivationTimestamp();
                    if (activatedSiteMap.has(s.SiteNumber)) {
                        activatedSiteMap.get(s.SiteNumber).StudyIds.push(s.StudyId);
                        activatedSiteMap.get(s.SiteNumber).ActivationTimestamps[s.StudyId] = s.ActivationTimestamp;
                    }
                    else {
                        let newSite: Site = new Site(s.SiteNumber, s.getSiteNameNumber());                        
                        newSite.StudyIds.push(s.StudyId);
                        newSite.ActivationTimestamps[s.StudyId] = s.ActivationTimestamp;
                        activatedSiteMap.set(s.SiteNumber, newSite);
                    }
                }
            });
            this.consentedSites = Array.from(consentedSiteMap.values()).sort((a: Site, b: Site) => a.Name.localeCompare(b.Name));
            this.activatedSites = Array.from(activatedSiteMap.values()).sort((a: Site, b: Site) => a.Name.localeCompare(b.Name));

            //console.log(this.consentedSites);
            //console.log(activatedSiteMap);
            //console.log(this.activatedSites);

            this.forecastRandomized = plainToClass(ForecastGraphDataPoint, <ForecastGraphDataPoint[]>randomizedAspirationalJson);            
            this.forecastRandomized = this.forecastRandomized.map((point: ForecastGraphDataPoint) => {
                point.initialize("first", "count");
                return point;
            }).filter((x: ForecastGraphDataPoint) => x.TypeId == 2 && x.IsOverall == 0); //only appendix-level as we aggregate
            this.selectedForecastRandomized = this.forecastRandomized;

            //console.log(this.sites);
            //console.log(this.participants);
            //console.log(this.studies);
            //console.log(this.appendices);
            //console.log(this.forecastRandomized);

            new DateUpdatedSection(lastUpdatedJson, false, true, false);

            this.changeSelectedPopulation("randomized", false);
            this.changeSelectedBreakdown("All Participants", '', false);
            this.changeSelectedAggregation("Overall", false);
            this.changeSelectedGraphView("Cumulative", false);

            this.progressBars = new ProgressBarSection();
            this.gauges = new EnrollmentGauges(this);
            this.chartController = new ChartController(this);

            //this.allAppendices.sort((a1: Appendix, a2: Appendix) => {
            //    return a1.Name.localeCompare(a2.Name);
            //});

            this.filter = new StudyAppendixSiteFilterSection("#study-dropdown", "#appendix-dropdown", "#site-dropdown", this.allStudies, this.allAppendices, this.consentedSites, this);

            $('#loading-indicator').hide();
            $('.loaded-content').removeClass('invisible');
            this.hideUnassignedToggleButton();

            this.initTotalEnrollmentModal();
            this.initCumulativeModal();
        });
    }

    changeSelectedPopulation(value: string, triggerRefresh: boolean = true): void {

        this.selectedPopulation = value;

        //need to change sorting for graphing
        if (value == "consented") {
            this.allParticipants = this.allParticipants.sort((p1, p2) => SortDates(p1.DateConsented, p2.DateConsented));
            $('#label-population').text("Consented");
            this.showUnassignedToggleButton();
        }
        else if (value == "randomized") {
            this.allParticipants = this.allParticipants.sort((p1, p2) => SortDates(p1.DateRandomized, p2.DateRandomized));
            $('#label-population').text("Randomized");
            this.hideUnassignedToggleButton();
        }
        else if (value == "screen-failed") {
            this.allParticipants = this.allParticipants.sort((p1, p2) => SortDates(p1.DateConsented, p2.DateConsented));
            $('#label-population').text("Screen Failed");
            this.showUnassignedToggleButton();
        }

        //console.log(this.participants);

        if (triggerRefresh) { this.filter?.refreshFilters(); }
    }

    changeSelectedBreakdown(newBreakdown: string, selectedDomainId: string = "", triggerRefresh: boolean = true): void {
        this.selectedBreakdown = newBreakdown;

        $('#chart-view-label').text((newBreakdown == "All Participants" ? newBreakdown : ("By " + newBreakdown)));
        $('.chart-details-toggle-button').removeClass('selected');

        let activateStudies: boolean = true;
        let activateAppendices: boolean = true;
        let activateSites: boolean = true;
        if (this.selectedBreakdown == "All Participants") {
            $('#view-all-no-breakdown').hide();

            //if (this.selectedGraphView == "Proportional" && this.selectedAggregation == "Overall") {
            //    this.changeSelectedAggregation("ByStudy", false);
            //}
            //else if (this.selectedGraphView == "RatePerSiteByMonth") { activateSites = false; }
            if (this.selectedGraphView == "RatePerSiteByMonth") { activateSites = false; }
            this.enableSelectionOption('#enrollment-graph-view', "RatePerSiteByMonth", true);
        }
        else
        {
            $('#view-all-no-breakdown').show();
            $('#' + selectedDomainId).addClass('selected');

            if (this.selectedGraphView != "Cumulative" && this.selectedGraphView != "Proportional") {
                activateStudies = activateAppendices = activateSites = false;
            }

            if (this.selectedAggregation != "Overall") { this.changeSelectedAggregation("Overall", false); }
            if (this.selectedGraphView == "RatePerSiteByMonth") { $('#enrollment-graph-view').val("Cumulative").trigger('change'); }
            this.enableSelectionOption('#enrollment-graph-view', "RatePerSiteByMonth", false);
        }
        this.enableButton('#view-studies-button', activateStudies);
        this.enableButton('#view-appendices-button', activateAppendices);
        this.enableButton('#view-sites-button', activateSites);

        if (triggerRefresh) { this.chartController.UpdateData(); }
    }

    changeSelectedAggregation(newAggregation: string, triggerRefresh: boolean = true): void {
        this.selectedAggregation = newAggregation;

        $('.aggregation-option').removeClass('active');
        if (this.selectedAggregation == "Overall") { $('#view-overall-button').addClass('active'); }
        if (this.selectedAggregation == "ByStudy") { $('#view-studies-button').addClass('active'); }
        if (this.selectedAggregation == "ByAppendix") { $('#view-appendices-button').addClass('active'); }
        if (this.selectedAggregation == "BySite") { $('#view-sites-button').addClass('active'); }

        if (triggerRefresh) { this.chartController.UpdateData(); }

        if (this.selectedAggregation != "Overall" && this.selectedBreakdown != "All Participants" && this.selectedGraphView == "Proportional") {
            this.showTotalEnrollmentModal();
        } else if (this.selectedAggregation != "Overall" && this.selectedBreakdown != "All Participants" && this.selectedGraphView == "Cumulative")  {
            this.showCumulativeModal();
        }
    }

    changeSelectedGraphView(newGraphView: string, triggerRefresh: boolean = true): void {

        this.selectedGraphView = newGraphView;

        if (this.selectedGraphView == "Cumulative" || this.selectedGraphView == "ByMonth" || this.selectedGraphView == "RatePerSiteByMonth") {
            let monthField: string = "";
            let countField: string = "";
            if (this.selectedGraphView == "Cumulative") { monthField = "last"; countField = "count"; }
            else if (this.selectedGraphView == "ByMonth") { monthField = "first"; countField = "count"; }
            else if (this.selectedGraphView == "RatePerSiteByMonth") { monthField = "first"; countField = "rate"; }

            this.forecastRandomized.forEach((x: ForecastGraphDataPoint) => { x.DayOfMonth = monthField; x.CountField = countField; });
            this.selectedForecastRandomized.forEach((x: ForecastGraphDataPoint) => { x.DayOfMonth = monthField; x.CountField = countField; });
        }
        else if (this.selectedGraphView == "Proportional") {
            //if (this.selectedAggregation == "Overall" && this.selectedBreakdown == "All Participants") {
            //    this.changeSelectedAggregation("ByStudy", false);
            //}
        }

        if (this.selectedGraphView == "RatePerSiteByMonth") {
            if (this.selectedAggregation == "BySite") { this.changeSelectedAggregation("Overall", false); }
            this.enableButton('#view-sites-button', false);
        }
        else if (this.selectedBreakdown == "All Participants") { this.enableButton('#view-sites-button', true); }

        if (this.selectedGraphView != "Cumulative" && this.selectedGraphView != "Proportional" && this.selectedBreakdown != "All Participants") {
            this.enableButton('#view-studies-button', false);
            this.enableButton('#view-appendices-button', false);
            this.enableButton('#view-sites-button', false);
        } else {
            this.enableButton('#view-studies-button', true);
            this.enableButton('#view-appendices-button', true);
            this.enableButton('#view-sites-button', true);
        }

        if (triggerRefresh) { this.chartController.UpdateData(); }
    }


    FilterUpdated(studyIds: number[], appendixIds: number[], siteIds: number[]) {
        //console.log("FilterUpdated", studyIds, appendixIds, siteIds);

        this.selectedParticipants = this.allParticipants.filter((p: Participant) => {
            if (this.selectedPopulation == "consented" && p.Consented != 1) return false;
            else if (this.selectedPopulation == "randomized" && p.Randomized != 1) return false;
            else if (this.selectedPopulation == "screen-failed" && p.ScreenFail != 1) return false;

            if (!studyIds.includes(p.StudyId)) return false;
            if (this.showUnassigned) {
                appendixIds = appendixIds.concat(this.unassignedAppendixIds);
            }
            if (!appendixIds.includes(p.AppendixId)) return false;
            if (!siteIds.includes(p.SiteNumber)) return false;

            return true;
        });

        //determine how many sites were activated for each study by month for monthly-reporting - only looking at select participants
        let monthTimestamps: number[] = [];
        this.selectedParticipants.forEach((p: Participant) => {
            if (this.selectedPopulation == "consented" || this.selectedPopulation == "screen-failed") {
                if (!monthTimestamps.includes(p.MonthConsented.getTime())) { monthTimestamps.push(p.MonthConsented.getTime()); }
            }
            else if (this.selectedPopulation == "randomized") {
                if (!monthTimestamps.includes(p.MonthRandomized.getTime())) { monthTimestamps.push(p.MonthRandomized.getTime()); }
            }
        })

        this.sitesActivatedByMonthByStudy.clear();
        let subsetOfSites: boolean = !siteIds.includes(0);
        this.activatedSites.forEach((s: Site) => {
            if (!DoArraysOverlap(s.StudyIds, studyIds)) return;
            if (subsetOfSites && !siteIds.includes(s.Id)) return;

            studyIds.forEach((studyId: number) => {
                monthTimestamps.forEach((monthTimestamp: number) => {
                    if (s.ActivationTimestamps[studyId] != undefined && s.ActivationTimestamps[studyId] <= monthTimestamp) {
                        let key: string = studyId + "_" + monthTimestamp;
                        let count: number = 0;
                        if (this.sitesActivatedByMonthByStudy.has(key)) {
                            count = this.sitesActivatedByMonthByStudy.get(key);
                        }
                        count += 1;
                        this.sitesActivatedByMonthByStudy.set(key, count);
                        //console.log(studyId + " | " + monthTimestamp + " | " + new Date(monthTimestamp) + " | " + count + " -- " + s.Id);
                        //if (monthTimestamp == 1704128400000 && studyId == 3) console.log(s.Id);
                    }
                });
            });            
        });
        //console.log(this.sitesActivatedByMonthByStudy);

        //forecasted information
        if (this.selectedPopulation == "randomized") {
            if (!siteIds.includes(0)) { this.selectedForecastRandomized = []; }
            else {
                this.selectedForecastRandomized = this.forecastRandomized.filter((x: ForecastGraphDataPoint) => appendixIds.includes(x.AppendixId));
            }
        }
        else { this.selectedForecastRandomized = []; }

        this.aggregatedParticipants.aggregate(this.selectedParticipants);
        //console.log(this.aggregatedParticipants);

        this.progressBars.updateProgressBarData(this.aggregatedParticipants);
        //console.log("StudyIds:", studyIds, "selectedPopulation", this.selectedPopulation);



        //console.log(studyIds);
        //if more than VITAL is selected, hide Symptom Clusters
        if (studyIds.length == 1 && studyIds[0] == 2 && this.selectedPopulation == "randomized") {
            //$('#sxcluster-exint').show(); $('#sxcluster-cogdys').show(); $('#sxcluster-autonomic').show();
            $('#symptom-cluster-container').show();
            $('#sleep-cluster-container').hide(); //s
            $('#autonomic-cluster-container').hide(); //s
            $('#energize-cluster-container').hide();
            this.gauges.UpdateData(this.aggregatedParticipants);
            $('#sxcluster-randomized-label-vital').text(FormatNumberWithCommas(String(this.selectedParticipants.length)));
            let percent: number = (this.selectedParticipants.length / 900) * 100;
            $('#sxcluster-percent-label-vital').text(FormatNumberWithCommas(String(Math.round(percent))));
        }
        //s
        else if (studyIds.length == 1 && studyIds[0] == 4 && this.selectedPopulation == "randomized") {
            $('#sleep-cluster-container').show();
            $('#symptom-cluster-container').hide();
            $('#autonomic-cluster-container').hide();
            $('#energize-cluster-container').hide();
            this.gauges.UpdateData(this.aggregatedParticipants);
            $('#sxcluster-randomized-label-sleep').text(FormatNumberWithCommas(String(this.selectedParticipants.length)));
            let newtarget: number;
            if (this.filter.selectedAppendixIds.length == 1) {
                if (this.filter.selectedAppendixIds[0] == 5) {
                    newtarget = this.gauges.sleep1Target;
                }
                else if (this.filter.selectedAppendixIds[0] == 6) {
                    newtarget = this.gauges.sleep2Target;
                } else {
                    newtarget = this.gauges.sleep1Target + this.gauges.sleep2Target;
                }
            }
            else {
                newtarget = this.gauges.sleep1Target + this.gauges.sleep2Target;
            }
            const sleeptarget = document.getElementById("sxcluster-total-target-sleep")
            if (sleeptarget) {
                sleeptarget.innerHTML = newtarget > 1000 ? FormatNumberWithCommas(newtarget.toString()) : newtarget.toString();
            }
            let percent: number = (this.selectedParticipants.length / newtarget) * 100;
            $('#sxcluster-percent-label-sleep').text(FormatNumberWithCommas(String(Math.round(percent))));

            if (this.filter.selectedAppendixIds.length == 1) {
                if (this.filter.selectedAppendixIds[0] == 5) {          
                    $('#card-sxcluster-sleep1').closest(".trial-container").show();
                    $('#card-sxcluster-sleep2').closest(".trial-container").hide();

                } else if (this.filter.selectedAppendixIds[0] == 6) {
                    $('#card-sxcluster-sleep2').closest(".trial-container").show();
                    $('#card-sxcluster-sleep1').closest(".trial-container").hide();
                }
            } else {
                $('#card-sxcluster-sleep1').closest(".trial-container").show();
                $('#card-sxcluster-sleep2').closest(".trial-container").show();
            }
            
        }
        else if (studyIds.length == 1 && studyIds[0] == 5 && this.selectedPopulation == "randomized") {
            $('#autonomic-cluster-container').show();
            $('#symptom-cluster-container').hide();
            $('#sleep-cluster-container').hide();
            $('#energize-cluster-container').hide();
            this.gauges.UpdateData(this.aggregatedParticipants);
            $('#sxcluster-randomized-label-autonomic').text(FormatNumberWithCommas(String(this.selectedParticipants.length)));
            let newtarget: number;
            if (this.filter.selectedAppendixIds.length == 1) {
                if (this.filter.selectedAppendixIds[0] == 8) {
                    newtarget = this.gauges.auto1target;
                }
                else if (this.filter.selectedAppendixIds[0] == 9) {
                    newtarget = this.gauges.auto2target;
                } else {
                    newtarget = this.gauges.auto1target + this.gauges.auto2target;
                }
            }
            else {
                newtarget = this.gauges.auto1target + this.gauges.auto2target;
            }
            const autonomictarget = document.getElementById("sxcluster-total-target-auto")
            if (autonomictarget) {
                autonomictarget.innerHTML = newtarget > 1000 ? FormatNumberWithCommas(newtarget.toString()) : newtarget.toString();
            }
            let percent: number = (this.selectedParticipants.length / newtarget) * 100;
            $('#sxcluster-percent-label-autonomic').text(FormatNumberWithCommas(String(Math.round(percent))));
            if (this.filter.selectedAppendixIds.length == 1) {
                if (this.filter.selectedAppendixIds[0] == 8) {
                    $('#card-sxcluster-auto1').closest(".trial-container").show();
                    $('#card-sxcluster-auto2').closest(".trial-container").hide();
                } else if (this.filter.selectedAppendixIds[0] == 9) {
                    $('#card-sxcluster-auto2').closest(".trial-container").show();
                    $('#card-sxcluster-auto1').closest(".trial-container").hide();
                }
            } else {
                $('#card-sxcluster-auto1').closest(".trial-container").show();
                $('#card-sxcluster-auto2').closest(".trial-container").show();
            }
        }
        else if (studyIds.length == 1 && studyIds[0] == 6 && this.selectedPopulation == "randomized") {
            $('#energize-cluster-container').show();
            $('#autonomic-cluster-container').hide();
            $('#symptom-cluster-container').hide();
            $('#sleep-cluster-container').hide();
            this.gauges.UpdateData(this.aggregatedParticipants);
            $('#sxcluster-randomized-label-energize').text(FormatNumberWithCommas(String(this.selectedParticipants.length)));
            let newtarget: number;
            if (this.filter.selectedAppendixIds.length == 1) {
                if (this.filter.selectedAppendixIds[0] == 11) {
                    newtarget = this.gauges.energize1target;
                }
                else if (this.filter.selectedAppendixIds[0] == 12) {
                    newtarget = this.gauges.energize2target;
                } else {
                    newtarget = this.gauges.energize1target + this.gauges.energize2target;
                }
            }
            else {
                newtarget = this.gauges.energize1target + this.gauges.energize2target;
            }
            const energizetarget = document.getElementById("sxcluster-total-target-energize")
            if (energizetarget) {
                energizetarget.innerHTML = newtarget > 1000 ? FormatNumberWithCommas(newtarget.toString()) : newtarget.toString();
            }
            let percent: number = (this.selectedParticipants.length / newtarget) * 100;
            $('#sxcluster-percent-label-energize').text(FormatNumberWithCommas(String(Math.round(percent))));

            if (this.filter.selectedAppendixIds.length == 1) {
                if (this.filter.selectedAppendixIds[0] == 11) {
                    $('#card-sxcluster-energize1').closest(".trial-container").show();
                    $('#card-sxcluster-energize2').closest(".trial-container").hide();
                } else if (this.filter.selectedAppendixIds[0] == 12) {
                    $('#card-sxcluster-energize2').closest(".trial-container").show();
                    $('#card-sxcluster-energize1').closest(".trial-container").hide();
                }
            } else {
                $('#card-sxcluster-energize1').closest(".trial-container").show();
                $('#card-sxcluster-energize2').closest(".trial-container").show();
            }
        }
        else {
            //$('#sxcluster-exint').hide(); $('#sxcluster-cogdys').hide(); $('#sxcluster-autonomic').hide();
            //if (this.selectedBreakdown.startsWith("SX Cluster")) {
            //    this.changeSelectedBreakdown("All Participants", "", false); //to make sure when SymClusters are hidden, if selected, not shown anymore
            //}
            $('#symptom-cluster-container').hide();
            $('#sleep-cluster-container').hide(); //s
            $('#autonomic-cluster-container').hide(); //s
            $('#energize-cluster-container').hide(); 
        }

        $('#value-total-population').text(FormatNumberWithCommas(String(this.selectedParticipants.length)));
               
        this.chartController.UpdateData();

        let sleep1target = 0, sleep2target = 0;
        let auto1target = 0, auto2target = 0;
        let energize1target = 0, energize2target = 0;

    }

    resetFilters(): void {
        this.showUnassigned = false;
        (document.getElementById("unassigned-switch") as HTMLInputElement).checked = false;
        $('#population-select').val("randomized");
        $('#enrollment-graph-view').val("Cumulative");

        this.changeSelectedPopulation("randomized", true);
        this.changeSelectedBreakdown("All Participants", '', true);
        this.changeSelectedAggregation("Overall", true);

        ResetDropdownFilters(['#study-dropdown', '#appendix-dropdown', '#site-dropdown']);

        this.changeSelectedGraphView('Cumulative', true);
    }

    toggleUnassignedAppendices(): void {
        this.showUnassigned = !this.showUnassigned;
        this.FilterUpdated(this.filter.selectedStudyIds, this.filter.selectedAppendixIds, this.filter.selectedSiteIds);
    }

    private enableButton(buttonId: string, active: boolean): void {
        $(buttonId).prop("disabled", !active);
        if (!active) { $(buttonId).addClass("disabled"); }
        else { $(buttonId).removeClass("disabled"); }
    }

    private enableSelectionOption(selectId: string, value: string, active: boolean): void {
        $(selectId + ' option').each(function () {
            if ($(this).val() == value) {
                if (active) { $(this).removeAttr('disabled'); }
                else { $(this).attr('disabled', 'disabled'); }                
                return;
            }
        });

        $(selectId).selectpicker('refresh');
    }

    showUnassignedToggleButton(): void {
        $('#unassigned-toggle').show();
        $('#reset-filters').removeClass("ml-auto");
    }

    hideUnassignedToggleButton(): void {
        $('#unassigned-toggle').hide();
        $('#reset-filters').addClass("ml-auto");
    }

    initTotalEnrollmentModal(): void {

        $("#total-enrollment-modal-container").empty();
        $("#total-enrollment-modal-container").append("<div class='col-12'><h6>Loading....</h6></div>")

        $("#total-enrollment-modal").on("shown.bs.modal", () => {
            $("#total-enrollment-modal-subheading").empty();
            $("#total-enrollment-modal-subheading").append(this.selectedBreakdown + " Breakdown By " + this.selectedAggregation.slice(2));

            $("#total-enrollment-modal-container").empty();
            $("#total-enrollment-modal-container").append("<div id='total-enrollment-modal-chart' > </div>");

            let chartSeries: ChartSeries[] = [];
            let xTitle: string = "";
            let yTitle: string = "";
            let xLabels: string[] = [];


            let selectedMap: Map<number, AggregatedParticipants> = new Map<number, AggregatedParticipants>();
            if (this.selectedAggregation == "ByStudy") {
                this.allStudies.forEach((study: Study) => {
                    let aggregatedData: AggregatedParticipants = this.aggParticipantsByStudy.get(study.Id);
                    if (!aggregatedData) { aggregatedData = new AggregatedParticipants(); }
                    aggregatedData.aggregate(this.selectedParticipants.filter((p: Participant) => p.StudyId == study.Id));
                    this.aggParticipantsByStudy.set(xLabels.length, aggregatedData);
                    xLabels.push(study.Name);
                });
                selectedMap = this.aggParticipantsByStudy;
                xTitle = "Study";
            } else if (this.selectedAggregation == "ByAppendix") {
                this.allAppendices.forEach((appendix: Appendix) => {
                    let aggregatedData: AggregatedParticipants = this.aggParticipantsByAppendix.get(appendix.Id);
                    if (!aggregatedData) { aggregatedData = new AggregatedParticipants(); }
                    aggregatedData.aggregate(this.selectedParticipants.filter((p: Participant) => p.AppendixId == appendix.Id));
                    this.aggParticipantsByAppendix.set(xLabels.length, aggregatedData);
                    xLabels.push(appendix.Name);
                });
                selectedMap = this.aggParticipantsByAppendix;
                xTitle = "Appendix";
            } else if (this.selectedAggregation == "BySite") {
                this.consentedSites.forEach((site: Site) => {
                    let aggregatedData: AggregatedParticipants = this.aggParticipantsBySite.get(site.Id);
                    if (!aggregatedData) { aggregatedData = new AggregatedParticipants(); }
                    aggregatedData.aggregate(this.selectedParticipants.filter((p: Participant) => p.SiteNumber == site.Id));
                    this.aggParticipantsBySite.set(xLabels.length, aggregatedData);
                    xLabels.push(site.Name);
                });
                selectedMap = this.aggParticipantsBySite;
                xTitle = "Site";
            }

            let indices = this.chartController.getIndices();

            selectedMap.forEach((aggregatedData: AggregatedParticipants, id: number) => {
                
                let data: number[] = [];
                if (this.selectedBreakdown == "RECOVER Cohort") { data = aggregatedData.RecoverCohort; }
                else if (this.selectedBreakdown == "Age") { data = aggregatedData.Age; }
                else if (this.selectedBreakdown == "Race") { data = aggregatedData.Race; }
                else if (this.selectedBreakdown == "Ethnicity") { data = aggregatedData.Hispanic; }
                else if (this.selectedBreakdown == "Hispanic or Non-White") { data = aggregatedData.NonWhiteHispanic; }
                else if (this.selectedBreakdown == "Sex") { data = aggregatedData.Sex; }
                else if (this.selectedBreakdown == "SX Cluster: ExInt (Enrolled)") { data = aggregatedData.ClusterExInt; }
                else if (this.selectedBreakdown == "SX Cluster: CogDys (Enrolled)") { data = aggregatedData.ClusterCogn; }
                else if (this.selectedBreakdown == "SX Cluster: Autonomic (Enrolled)") { data = aggregatedData.ClusterAuto; }

                indices.forEach((index: number) => {
                    let label: string = "";
                    if (this.selectedBreakdown == "RECOVER Cohort" || this.selectedBreakdown == "Ethnicity" || this.selectedBreakdown == "Hispanic or Non-White" || this.selectedBreakdown == "SX Cluster: ExInt (Enrolled)" || this.selectedBreakdown == "SX Cluster: CogDys (Enrolled)" || this.selectedBreakdown == "SX Cluster: Autonomic (Enrolled)") {
                        label = EnrollmentLookup.GetYesNo(index);
                        if (this.selectedBreakdown == "Ethnicity" && label != "Unknown") {
                            label = (label == "Yes") ? "Hispanic" : "Not Hispanic";
                        }
                    }
                    else if (this.selectedBreakdown == "Sex") {
                        label = EnrollmentLookup.GetSex(index);
                    }
                    else if (this.selectedBreakdown == "Age") {
                        label = EnrollmentLookup.GetAgeGroup(index);
                    }
                    else if (this.selectedBreakdown == "Race") {
                        label = EnrollmentLookup.GetRace(index);
                    }

                    let series = new ChartSeries(label);
                    if (chartSeries.filter((s: ChartSeries) => s.name == label).length > 0) {
                        series = chartSeries.filter((s: ChartSeries) => s.name == label)[0];
                    } else {
                        series.data = [];
                        series.type = 'bar';
                        series.name = label;
                        series.color = this.chartController.getBreakdownColor(index);
                        chartSeries.push(series);
                    }

                    series.data.push(new ChartDataPoint(id, data[index]));

                });


            });

            $('#total-enrollment-modal-chart').height(selectedMap.size * 50 + 100); //dynamically set height based on number of bars)

            let setup = new ChartSetup(chartSeries, false, false, true);

            this.totEnrollDrilldownChart.UpdateChart(setup, xTitle, yTitle, xLabels);
        });

        
        $("#total-enrollment-modal").on("hide.bs.modal", () => {
            $("#total-enrollment-modal-container").empty();
            $("#total-enrollment-modal-container").append("<div class='col-12'><h6>Loading....</h6></div>")
            this.changeSelectedAggregation("Overall");
        });
    }

    showTotalEnrollmentModal(): void {
        $("#total-enrollment-modal").modal('show');
    }


    initCumulativeModal(): void {

        $("#cumulative-modal-container").empty();
        $("#cumulative-modal-container").append("<div class='col-12'><h6>Loading....</h6></div>")

        $("#cumulative-modal").on("shown.bs.modal", () => {

            $("#cumulative-modal-subheading").empty();
            $("#cumulative-modal-subheading").append(this.selectedBreakdown + " Breakdown By " + this.selectedAggregation.slice(2));


            $("#cumulative-modal-container").empty();

            let chartBuilder: ChartLine = new ChartLine();

            var aggregatedCatMap: Map<number, string> = new Map<number, string>();
            var selectorFunc: (p: Participant) => boolean = null;
            var selectedIds: number[] = []
            var currID: number = 0;
            if (this.selectedAggregation == "ByStudy") {
                this.allStudies.forEach((study: Study) => { aggregatedCatMap.set(study.Id, study.Name); });
                selectorFunc = (p: Participant) => p.StudyId == currID;
                selectedIds = this.filter.selectedStudyIds;
            } else if (this.selectedAggregation == "ByAppendix") {
                this.allAppendices.forEach((appendix: Appendix) => { aggregatedCatMap.set(appendix.Id, appendix.Name); });
                selectorFunc = (p: Participant) => p.AppendixId == currID;
                selectedIds = this.filter.selectedAppendixIds;
            } else if (this.selectedAggregation == "BySite") {
                this.consentedSites.forEach((site: Site) => { aggregatedCatMap.set(site.Id, site.Name); });
                selectorFunc = (p: Participant) => p.SiteNumber == currID;
                selectedIds = this.filter.selectedSiteIds;
            }


            let charts: [id: number, setup: ChartSetup, total: number][]  = [];

            aggregatedCatMap.forEach((name: string, id: number) => {
                if (selectedIds.filter(x => x == id).length == 0) {
                    return
                }
                currID = id;
                let filteredParticipants = this.selectedParticipants.filter(selectorFunc);
                let chartSetup: ChartSetup = this.chartController.createSeriesForOverall(filteredParticipants);

                let total: number = 0;
                chartSetup.series.forEach((s: ChartSeries) => {
                    if (s.data[s.data.length - 1]) {
                        total += s.data[s.data.length-1].y;
                    }
                });

                charts.push([id, chartSetup, total]);
            });

            charts.sort((a, b) => b[2] - a[2]);

            charts.forEach((c: [id: number, setup: ChartSetup, total: number]) => {
                $("#cumulative-modal-container").append("<div id='cumulative-modal-chart-" + c[0] + "' class='col-6'></div>")
                chartBuilder.UpdateChart(c[1], aggregatedCatMap.get(c[0]) + " - Total: " + c[2], "cumulative-modal-chart-" + c[0]);
            });

        });

        $("#cumulative-modal").on("hide.bs.modal", () => {
            $("#cumulative-modal-container").empty();
            $("#cumulative-modal-container").append("<div class='col-12'><h6>Loading....</h6></div>")
            this.changeSelectedAggregation("Overall");
        });
    }

    showCumulativeModal(): void {
        $("#cumulative-modal").modal('show');
    }
}

export interface IEnrollmentPageData {
    allStudies: Study[];
    allAppendices: Appendix[];

    consentedSites: Site[];
    activatedSites: Site[];
    //siteActivationStudyTimestampMap: Map<number, number[]>;
    sitesActivatedByMonthByStudy: Map<string, number>; //key = studyId_dateTimestamp, value = count

    //allParticipants: Participant[];
    selectedParticipants: Participant[];
    aggregatedParticipants: AggregatedParticipants;
     
    //forecastRandomized: ForecastGraphDataPoint[];
    selectedForecastRandomized: ForecastGraphDataPoint[];

    selectedPopulation: string; //consented, randomized, screen-fail
    selectedBreakdown: string; // "Overall", "Race", "Age", etc...
    selectedAggregation: string; // overall, ByStudy, ByAppendix, BySite
    selectedGraphView: string; //cumulative (line), pie chart, by month (bar), rate by month (bar)
    unassignedAppendixIds: number[];
    showUnassigned: boolean;
    unassignedAppendicies: Appendix[];

}